// Boss HP HUD — top-center persistent bar during the boss room
// (Phase 5 Step 17d).
//
// Extracted from combat-ui.ts to keep that file under the §10 400-line
// cap. Reads hp from state.mobHp[state.bossMobId]; visible iff that id
// is non-null AND an entry exists. Single bar — Phase 5 has at most
// one boss per expedition, so no tab switching.

import { Application, Container, Graphics, Text } from "pixi.js";
import type { CombatClientState } from "./combat-state.ts";

const BOSS_HP_WIDTH_PX = 320; // contracts:allow-literal — boss HP bar width
const BOSS_HP_HEIGHT_PX = 18; // contracts:allow-literal — boss HP bar height
const BOSS_HP_TOP_MARGIN_PX = 12; // contracts:allow-literal — top margin
const BOSS_HP_BG = 0x333333;
const BOSS_HP_FILL = 0xff6677;

export class BossHpHUD {
  private readonly container = new Container();
  private readonly fill: Graphics;
  private readonly text: Text;

  constructor(private readonly app: Application) {
    this.container.zIndex = 100; // contracts:allow-literal — above world, below modals
    app.stage.addChild(this.container);

    const bg = new Graphics()
      .rect(0, 0, BOSS_HP_WIDTH_PX, BOSS_HP_HEIGHT_PX)
      .fill({ color: BOSS_HP_BG });
    this.container.addChild(bg);

    this.fill = new Graphics()
      .rect(0, 0, BOSS_HP_WIDTH_PX, BOSS_HP_HEIGHT_PX)
      .fill({ color: BOSS_HP_FILL });
    this.fill.scale.x = 1;
    this.container.addChild(this.fill);

    this.text = new Text({
      text: "",
      style: { fontSize: 13, fill: 0xffffff, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — boss HUD font
    });
    this.container.addChild(this.text);

    this.container.visible = false;
    this.layout();
    app.renderer.on("resize", () => this.layout());
  }

  update(state: CombatClientState): void {
    if (state.bossMobId === null) {
      this.container.visible = false;
      return;
    }
    const hp = state.mobHp.get(state.bossMobId);
    if (hp === undefined) {
      this.container.visible = false;
      return;
    }
    this.container.visible = true;
    this.fill.scale.x = hp.max > 0 ? Math.max(0, hp.current / hp.max) : 0;
    this.text.text = `BOSS ${hp.current} / ${hp.max}`;
    this.text.x = (BOSS_HP_WIDTH_PX - this.text.width) / 2; // contracts:allow-literal — centering idiom
    this.text.y = (BOSS_HP_HEIGHT_PX - this.text.height) / 2; // contracts:allow-literal — centering idiom
  }

  private layout(): void {
    const screenW = this.app.renderer.width;
    this.container.x = (screenW - BOSS_HP_WIDTH_PX) / 2; // contracts:allow-literal — centering idiom
    this.container.y = BOSS_HP_TOP_MARGIN_PX;
  }
}
